import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask, task, timeout } from 'ember-concurrency';
import { v1 } from 'ember-uuid';
import LightboxOverlay from 'sh-www/components/modals/overlays/lightbox';
import { USER } from 'sh-www/models/user';
import { showModal } from 'sh-www/utils/modal-controls';
import { mapResponseToCollection } from 'sh-www/utils/store';
import { APPLICATION_STATUS, APPLICATION_SIMPLE_STATUS } from 'sh-www/models/application';
import { isUrl } from 'sh-www/utils/misc';

const SORT_OPTIONS = {
  'last-activity': 'Sort by last updated',
};

const PROJECT_INCLUDES = [
  'assignment',
  'pitch',
  'projectdigest.classifications',
  'projectdigest.company',
  'projectdigest.oembed',
  'projectdigest.projectdigest-verifications:verified_or_latest',
  'projectdigest.review.reviewer',
  'projectdigest.services',
  'seller',
  'thread',
];

const QUERY_PARAMS = [
  'status',
  'simple_status',
  'company_id',
  'buyer_id',
  'seller_id',
  'sort',
  'secondary_sort',
];

const PAGE_SIZE = 10;

const optionsModel = (value, name) => ({ name, value });
const formattedOption = (name, value) => `${name} [${value}]`;

const STATUS_OPTIONS = [
  optionsModel( '', 'Any'),
  optionsModel(APPLICATION_STATUS.PENDING, formattedOption('pending', APPLICATION_STATUS.PENDING)),
  optionsModel(APPLICATION_STATUS.PUBLISHER_APPROVED, formattedOption('publisher_approved', APPLICATION_STATUS.PUBLISHER_APPROVED)),
  optionsModel(APPLICATION_STATUS.PRODUCER_APPROVED, formattedOption('producer_approved', APPLICATION_STATUS.PRODUCER_APPROVED)),
  optionsModel(APPLICATION_STATUS.PUBLISHER_APPROVED_PRICE, formattedOption('publisher_approved_price', APPLICATION_STATUS.PUBLISHER_APPROVED_PRICE)),
  optionsModel(APPLICATION_STATUS.ACTIVE, formattedOption('active', APPLICATION_STATUS.ACTIVE)),
  optionsModel(APPLICATION_STATUS.COMPLETE_PRODUCER, formattedOption('complete_producer', APPLICATION_STATUS.COMPLETE_PRODUCER)),
  optionsModel(APPLICATION_STATUS.COMPLETE_PUBLISHER, formattedOption('complete_publisher', APPLICATION_STATUS.COMPLETE_PUBLISHER)),
  optionsModel(APPLICATION_STATUS.COMPLETED, formattedOption('completed', APPLICATION_STATUS.COMPLETED)),
  optionsModel(APPLICATION_STATUS.PAID, formattedOption('paid', APPLICATION_STATUS.PAID)),
  optionsModel(APPLICATION_STATUS.DECLINED, formattedOption('declined', APPLICATION_STATUS.DECLINED)),
  optionsModel(APPLICATION_STATUS.CANCEL_PENDING, formattedOption('cancel_pending', APPLICATION_STATUS.CANCEL_PENDING)),
  optionsModel(APPLICATION_STATUS.CANCELLED, formattedOption('cancelled', APPLICATION_STATUS.CANCELLED)),
];

const SIMPLE_STATUS_OPTIONS = [
  optionsModel('', 'Any'),
  optionsModel(APPLICATION_SIMPLE_STATUS.ACTIVE, 'Active'),
  optionsModel(APPLICATION_SIMPLE_STATUS.ARCHIVED, 'Archived'),
  optionsModel(APPLICATION_SIMPLE_STATUS.CANCELLED, 'Cancelled'),
  optionsModel(APPLICATION_SIMPLE_STATUS.COMPLETE, 'Completed'),
  optionsModel(APPLICATION_SIMPLE_STATUS.COMPLETE_PRODUCER, 'Completed By Producer'),
  optionsModel(APPLICATION_SIMPLE_STATUS.DECLINED, 'Declined'),
  optionsModel(APPLICATION_SIMPLE_STATUS.FINAL, 'Final'),
  optionsModel(APPLICATION_SIMPLE_STATUS.HIRED, 'Hired'),
  optionsModel(APPLICATION_SIMPLE_STATUS.IN_PROGRESS, 'In Progress'),
  optionsModel(APPLICATION_SIMPLE_STATUS.NEEDS_ACTION_BUYER, 'Needs Buyer Action'),
  optionsModel(APPLICATION_SIMPLE_STATUS.NEEDS_ACTION_SELLER, 'Needs Seller Action'),
  optionsModel(APPLICATION_SIMPLE_STATUS.PAID, 'Paid'),
  optionsModel(APPLICATION_SIMPLE_STATUS.PENDING, 'Pending'),
  optionsModel(APPLICATION_SIMPLE_STATUS.PENDING_BUYER, 'Pending Buyer'),
  optionsModel(APPLICATION_SIMPLE_STATUS.PENDING_SELLER, 'Pending Seller'),
];

const initializeMap = (keys, initialValue) => keys.reduce((accum, x) => {
  accum[x] = initialValue;
  return accum;
}, { });

const mapSelectedOptions = (options, selected) => Object.entries(options).map(([value, title]) => ({
  title,
  value,
  selected: value === selected,
}));

class AdminProjectsController extends Controller {
  queryParams = [...QUERY_PARAMS];

  status = null;
  simple_status = null;
  company_id = null;
  buyer_id = null;
  seller_id = null;
  sort = null;
  secondary_sort = null;

  page = 1;
  filter_by = 'status';

  @service('video-thumbnail-from-url')
  videoThumbnailService;

  @service('state-managers.application')
  applicationStateManager;

  @service
  api;

  @service
  store;

  get statusOptions() {
    return STATUS_OPTIONS;
  }

  get simpleStatusOptions() {
    return SIMPLE_STATUS_OPTIONS;
  }

  @computed('sort')
  get sortValue() {
    return this.sort || 'last-activity';
  }

  set sortValue(value) {
    this.setProperties({
      sort: value === 'last-activity' ? null : value,
      secondary_sort: null,
    });
  }

  @computed('secondary_sort')
  get secondarySortValue() {
    return this.secondary_sort || 'desc';
  }

  set secondarySortValue(value) {
    this.set('secondary_sort', value === 'desc' ? null : value);
  }

  @computed('sortValue')
  get sortOptions() {
    return mapSelectedOptions(SORT_OPTIONS, this.sortValue);
  }

  @computed('loadSearchResults.last.value.data.[]')
  get applications() {
    return this.loadSearchResults.last?.value?.data ?? [];
  }

  @computed('loadSearchResults.last.value.meta.pagination.total')
  get total() {
    return this.loadSearchResults.last?.value?.meta?.pagination.total ?? 0;
  }

  @computed('loadSearchResults.last.value.meta.pagination.total_pages')
  get totalPages() {
    return this.loadSearchResults.last?.value?.meta?.pagination.total_pages ?? 0;
  }

  @computed('page')
  get disablePreviousPage() {
    return this.page <= 1;
  }

  @computed('page', 'totalPages')
  get disableNextPage() {
    return this.page >= this.totalPages;
  }

  @computed('page', 'total')
  get pageControlsDisplayString() {
    const { total, page } = this;

    const displayPageMin = (page - 1) * PAGE_SIZE + 1;
    const displayPageMax = Math.min(page * PAGE_SIZE, total);

    return `${displayPageMin} - ${displayPageMax} of ${total}`;
  }

  @computed('lookupCompaniesByName.lastSuccessful.value.@each.{id,name}')
  get companyOptions() {
    return this.lookupCompaniesByName.lastSuccessful?.value?.map((company) => ({
      key: `${company.name} (${company.id})`,
      id: company.id,
    }));
  }

  @computed('lookupBuyer.lastSuccessful.value.@each.{id,fullName}')
  get buyerOptions() {
    return this.lookupBuyer.lastSuccessful?.value?.map((user) => ({
      key: `${user.fullName} (${user.id})`,
      id: user.id,
    }));
  }

  @computed('lookupSeller.lastSuccessful.value.@each.{id,fullName}')
  get sellerOptions() {
    return this.lookupSeller.lastSuccessful?.value?.map((user) => ({
      key: `${user.fullName} (${user.id})`,
      id: user.id,
    }));
  }

  @computed(...QUERY_PARAMS)
  get cacheKey() {
    // entangle props
    QUERY_PARAMS.forEach((key) => this[key]);
    return v1();
  }

  @computed(...QUERY_PARAMS)
  get hasFilters() {
    return QUERY_PARAMS.some((key) => Boolean(this[key]));
  }

  @action
  getProjectDigestThumbnail(projectDigest) {
    const thumbnail = projectDigest.thumbnailUrl;

    if (thumbnail) {
      return thumbnail;
    }

    const link = projectDigest.oembed?.url ?? projectDigest.link;

    if (!link || !isUrl(link)) {
      return null;
    }

    return this.videoThumbnailService.getThumb(link);
  }

  @action
  showProjectDigest(projectDigest) {
    if (projectDigest.isEmbedVideo || projectDigest.isLinkableVideo) {
      showModal(this, LightboxOverlay, {
        digestsToShowForVideoCarousel: [projectDigest],
      });
    }
  }

  @action
  async copyProjectDigestLink(projectDigest) {
    try {
      const link = projectDigest.oembed?.url ?? projectDigest.link;
      await navigator.clipboard.writeText(link);
      this.applicationStateManager.addToast('Link copied to clipboard');
    } catch {
      this.applicationStateManager.addToast('Failed to copy link');
    }
  }

  @action
  setStatus(status) {
    this.setProperties({
      status: status || null,
      simple_status: null,
      page: 1,
    });
  }

  @action
  setSimpleStatus(simple_status) {
    this.setProperties({
      status: null,
      simple_status: simple_status || null,
      page: 1,
    });
  }

  @action
  setPage(page, increment) {
    this.set('page', page + increment);
  }

  @action
  setFilter(filter_by) {
    this.set('filter_by', filter_by);
  }

  @action
  handleCompanySelect(company_id) {
    this.lookupCompaniesByName.cancelAll({ resetState: true });
    this.setProperties({ company_id, page: 1 });
  }

  @action
  handleUserSelect(buyerOrSeller, id) {
    switch (buyerOrSeller) {
      case 'buyer_id':
        this.lookupBuyer.cancelAll({ resetState: true });
        break;

      case 'seller_id':
        this.lookupSeller.cancelAll({ resetState: true });
        break;
    }

    this.setProperties({ [buyerOrSeller]: id, page: 1 });
  }

  @action
  handleReset(e) {
    e.preventDefault();

    this.setProperties({
      ...initializeMap(QUERY_PARAMS, null),
      page: 1,
      filter_by: 'status',
    });
  }

  @restartableTask
  *lookupBuyer(idSlugEmail) {
    return yield this.lookupUser.perform(USER.TYPES.BUYER, idSlugEmail);
  }

  @restartableTask
  *lookupSeller(idSlugEmail) {
    return yield this.lookupUser.perform(USER.TYPES.SELLER, idSlugEmail);
  }

  @restartableTask
  *loadSearchResults(status, simple_status, company_id, buyer_id, seller_id, sort, secondary_sort, page, cache_key) {
    const params = {
      page,
      sort,
      secondary_sort,
      cache_key,
      adminSearch: true,
      count: PAGE_SIZE,
      include: PROJECT_INCLUDES.join(','),
    }

    if (status) {
      (params.filter ??= { }).status = status;
    }

    if (simple_status) {
      (params.filter ??= { }).simple_status = simple_status;
    }

    if (company_id) {
      (params.filter ??= { }).company_id = company_id;
    }

    if (buyer_id) {
      (params.filter ??= { }).buyer_id = buyer_id;
    }

    if (seller_id) {
      (params.filter ??= { }).seller_id = seller_id;
    }

    return mapResponseToCollection(this, 'application', yield this.api.get('applications', { params }), {
      preserve: true,
    });
  }

  @restartableTask
  *lookupCompaniesByName(name) {
    if (!name) {
      return null;
    }

    yield timeout(150);

    return mapResponseToCollection(this, 'company', yield this.api.get('companies', {
      params: {
        filter: { name },
        page: 1,
        include: 'groups',
      },
    }));
  }

  @task
  *lookupUser(type, idSlugEmail) {
    if (!idSlugEmail) {
      return null;
    }

    yield timeout(150);

    return mapResponseToCollection(this, 'user', yield this.api.get('users', {
      params: {
        filter: { idSlugEmail, type },
        page: 1,
      },
    }));
  }
}

export default AdminProjectsController;
